import { CommonModule } from '@angular/common';
import { HttpResponse } from '@angular/common/http';
import { Component, computed, HostListener, OnDestroy, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Observable, Subject, Subscription } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { CompanyService } from '../../../core/services/api-services/company.service';
import { StatisticsService } from '../../../core/services/api-services/statistics.service';
import { PaginatorComponent } from "../../../shared/components/paginator/paginator.component";
import { EmployeeWalletSummary } from './models/employee-wallet-summary';
import { ToastrService } from 'ngx-toastr';
import { BreadcrumbItem, BreadcrumbComponent } from '../../../shared/components/breadcrumb/breadcrumb.component';
import { BreadcrumbService } from '../../../shared/components/breadcrumb/service/breadcrumb.service';

type ReportDownloadType = 'walletSummary' | 'walletIn' | 'walletOut';

const ALL_COMPANIES_SENTINEL = { companyId: -1, companyName: 'All Companies' } as const;

@Component({
  selector: 'app-wallet-withdrawals',
  standalone: true,
  imports: [CommonModule, FormsModule, PaginatorComponent, BreadcrumbComponent],
  templateUrl: './wallet-withdrawals.component.html',
  styleUrls: ['./wallet-withdrawals.component.css']
})
export class WalletWithdrawalsComponent implements OnInit, OnDestroy {

  breadcrumbItems: BreadcrumbItem[] = [];
  private destroy$ = new Subject<void>();

  // ───────────────────────────────────────────────────────────────
  // Tab management
  // ───────────────────────────────────────────────────────────────
  activeTab: 'walletStatus' | 'reports' = 'walletStatus';

  // ── Reports searchable dropdown ──────────────────────────────────────────
  reportCompanySearchTerm = signal('');
  reportCompanyDropdownOpen = false;

  filteredReportCompanies = computed(() => {
    const term = this.reportCompanySearchTerm().toLowerCase().trim();

    // "All Companies" always appears at the top, filtered only when the search
    // term doesn't match at all.
    // Temporarily commented out — All Companies option disabled
    // const allOption = ALL_COMPANIES_SENTINEL as any;
    // const showAll = !term || 'all companies'.includes(term);

    // if (!term) return this.reportsCompanies;
    // return this.reportsCompanies.filter(c =>
    //   c.companyName.toLowerCase().includes(term) ||
    //   String(c.companyId).includes(term)
    // );

    const matched = this.reportsCompanies.filter(c =>
      c.companyName.toLowerCase().includes(term) ||
      String(c.companyId).includes(term)
    );

    // return showAll ? [allOption, ...matched] : matched;
    return matched;
  });

  // ───────────────────────────────────────────────────────────────
  // Date Range selection for Company Withdrawals
  // ───────────────────────────────────────────────────────────────
  companyStartDate: string = '';
  companyEndDate: string = '';
  companyMaxDate: string = '';
  companyMinEndDate: string = '';

  // ───────────────────────────────────────────────────────────────
  // Month/Year selection
  // ───────────────────────────────────────────────────────────────
  selectedYear: number = new Date().getFullYear();
  selectedMonth: number = new Date().getMonth() + 1; // 1-12
  years: number[] = [];
  months = [
    { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
    { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
    { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
    { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' }
  ];

  private readonly reportTypeLabels: Record<ReportDownloadType, string> = {
    walletSummary: 'Wallet Summary Report',
    walletIn: 'Wallet In Report',
    walletOut: 'Wallet Out Report'
  };

  // ───────────────────────────────────────────────────────────────
  // Companies list
  // ───────────────────────────────────────────────────────────────
  isCompaniesLoading = false;
  companiesError = '';
  companies: any[] = [];
  filteredCompanies: any[] = [];
  pagedCompanies: any[] = [];
  companySearchText = '';
  companiesPage = 1;
  companiesPageSize = 10;

  expandedCompanyIds = new Set<number>();

  // ───────────────────────────────────────────────────────────────
  // Employees per company (lazy, cached)
  // ───────────────────────────────────────────────────────────────
  employeesByCompany = new Map<number, Array<EmployeeWalletSummary>>();
  isEmployeesLoadingMap: Record<number, boolean> = {};
  employeesErrorMap: Record<number, string> = {};

  // ── Employee sub-table: filter & in-memory pagination state ───
  empFilterMap: Record<number, string> = {};
  empCurrentPageMap: Record<number, number> = {};
  empPageSizeMap: Record<number, number> = {};

  // ───────────────────────────────────────────────────────────────
  // Employee withdrawals offcanvas
  // ───────────────────────────────────────────────────────────────
  selectedCompany: any = null;
  selectedEmployee: any = null;
  isWithdrawalsLoading = false;
  withdrawalsError = '';
  withdrawals: any[] = [];
  filteredWithdrawals: any[] = [];
  pagedWithdrawals: any[] = [];
  withdrawalsPage = 1;
  withdrawalsPageSize = 10;
  withdrawalSearchText = '';

  dataSubscriber$!: Subscription;

  systemDate: Date = new Date();

  constructor(
    private statisticsService: StatisticsService,
    private companyService: CompanyService,
    private toaster: ToastrService,
    private breadcrumbService: BreadcrumbService
  ) {

    this.dataSubscriber$ = new Subscription();
  }


  ngOnInit(): void {

    // Subscribe to breadcrumb changes
    this.breadcrumbService.breadcrumbs$
      .pipe(takeUntil(this.destroy$))
      .subscribe(items => {
        this.breadcrumbItems = items;
      });

    // Set breadcrumbs for this page
    this.breadcrumbService.setBreadcrumbs([
      { label: 'Dashboard', url: '/dashboard', icon: 'bi bi-house-door' },
      { label: 'Treasury', icon: 'bi bi-bank' },
      { label: 'Wallet Status', icon: 'bi bi-wallet2' }
    ]);

    this.setCompanyStartAndEndDates();
    // build last 5 years
    const currentYear = new Date().getFullYear();
    for (let y = currentYear; y >= currentYear - 5; y--) this.years.push(y);
    this.fetchCompanies();
  }

  setCompanyStartAndEndDates(): void {
    const today = new Date();
    // Check if today is the 1st day of the month
    const isFirstDay = today.getDate() === 1;

    let startDate: Date;
    let endDate: Date = today;

    // // If today is 1st → use previous month's 1st day
    // if (isFirstDay) {
    //   startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    // } else {
    //   // Otherwise → current month's 1st
    //   startDate = new Date(today.getFullYear(), today.getMonth(), 1);
    // }

    startDate = this.formatDate(new Date(2025, 9, 1)); // "2025-10-01" -> Fixed as the whole system start withrawals date is 2025-10-01, so we set it as default start date for better UX. Admin can change it if needed.

    // Convert to yyyy-mm-dd or any format you want
    const format = (d: Date) =>
      d.toISOString().split("T")[0];

    this.companyStartDate = format(startDate);
    this.companyEndDate = format(endDate);
  }

  formatDate(date: Date): Date {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0'); // month +1
    const day = date.getDate().toString().padStart(2, '0');
    return new Date(`${year}-${month}-${day}`);
  }

  // ───────────────────────────────────────────────────────────────
  // Initialize date range to current month start and today
  // ───────────────────────────────────────────────────────────────
  initializeCompanyDateRange(): void {
    const today = new Date();

    // Set max date to today
    this.companyMaxDate = this.formatDateForInput(today);

    // Get first day of current month
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    // Set default date range: first day of current month to today
    this.companyStartDate = this.formatDateForInput(firstDayOfMonth);
    this.companyEndDate = this.formatDateForInput(today);

    // Set min end date
    this.companyMinEndDate = this.companyStartDate;
  }

  // ───────────────────────────────────────────────────────────────
  // Format date helper
  // ───────────────────────────────────────────────────────────────
  formatDateForInput(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // ───────────────────────────────────────────────────────────────
  // Company date range change handler
  // ───────────────────────────────────────────────────────────────
  onCompanyDateRangeChange(): void {

    // Check if end date is selected without start date
    if (!this.companyStartDate && this.companyEndDate) {
      this.toaster.warning('Please select start date first.', 'Warning');
      this.companyEndDate = ''; // Clear the end date
      return;
    }

    // Update min end date when start date changes
    if (this.companyStartDate) {
      // Set min end date to one day after start date
      const nextDay = new Date(this.companyStartDate);
      nextDay.setDate(nextDay.getDate() + 1);
      this.companyMinEndDate = nextDay.toISOString().split('T')[0];

      // If end date is before or equal to start date, clear it and show warning
      if (this.companyEndDate) {
        const startDate = new Date(this.companyStartDate);
        const endDate = new Date(this.companyEndDate);

        if (endDate <= startDate) {
          this.toaster.warning('End date must be after start date.', 'Warning');
          this.companyEndDate = '';
          return; // Don't fetch until valid range is selected
        }
      }
    }

    // Only fetch if both dates are selected and valid
    if (this.companyStartDate && this.companyEndDate && !this.hasInvalidCompanyDateRange()) {
      this.expandedCompanyIds.clear();
      this.employeesByCompany.clear();
      this.empFilterMap = {};
      this.empCurrentPageMap = {};
      this.empPageSizeMap = {};
      this.fetchCompanies();
    }
  }

  // ───────────────────────────────────────────────────────────────
  // Validate company date range
  // ───────────────────────────────────────────────────────────────
  hasInvalidCompanyDateRange(): boolean {
    if (!this.companyStartDate || !this.companyEndDate) {
      this.filteredCompanies = [];
      return false;
    }
    const startDate = new Date(this.companyStartDate);
    const endDate = new Date(this.companyEndDate);

    // Invalid if end date is before OR equal to start date
    return endDate <= startDate;
  }

  // ───────────────────────────────────────────────────────────────
  // Reset company date filters
  // ───────────────────────────────────────────────────────────────
  resetCompanyDateFilters(): void {
    this.initializeCompanyDateRange();
    this.expandedCompanyIds.clear();
    this.employeesByCompany.clear();
    this.empFilterMap = {};
    this.empCurrentPageMap = {};
    this.empPageSizeMap = {};
    this.setCompanyStartAndEndDates();
    this.fetchCompanies();
  }

  get selectedMonthLabel(): string {
    const m = this.months.find(mo => mo.value === this.selectedMonth);
    return m ? m.label : '';
  }

  // ───────────────────────────────────────────────────────────────
  // Month/Year change
  // ───────────────────────────────────────────────────────────────
  onMonthYearChange(): void {
    this.expandedCompanyIds.clear();
    this.employeesByCompany.clear();
    this.empFilterMap = {};
    this.empCurrentPageMap = {};
    this.empPageSizeMap = {};
    this.fetchCompanies();
  }

  // // ───────────────────────────────────────────────────────────────
  // // Companies
  // // ───────────────────────────────────────────────────────────────
  // ───────────────────────────────────────────────────────────────
  // Fetch companies with date range
  // ───────────────────────────────────────────────────────────────
  fetchCompanies(): void {
    this.isCompaniesLoading = true;
    this.companiesError = '';

    // console.log('Fetching companies with date range:', this.companyStartDate, this.companyEndDate);

    this.dataSubscriber$.add(
      this.statisticsService.getCompanyWalletWithdrawalTotalsByDateRange(
        this.companyStartDate,
        this.companyEndDate
      ).subscribe({
        next: (response: any) => {
          if (response?.success) {
            this.companies = (response.data ?? []) as any[];
            this.filteredCompanies = [...this.companies];
            this.resetCompaniesPagination();
          } else {
            this.filteredCompanies = [];
            this.companiesError = response?.message || 'Could not load company wallet totals.';
          }
          this.isCompaniesLoading = false;
        },
        error: (err) => {
          this.filteredCompanies = [];
          this.companiesError = this.getUserFriendlyError(err, 'Could not load company wallet totals.');
          this.isCompaniesLoading = false;
        }
      })
    );
  }

  performCompanySearch(): void {
    const q = (this.companySearchText || '').trim().toLowerCase();
    if (!q) {
      this.filteredCompanies = [...this.companies];
      this.resetCompaniesPagination();
      return;
    }
    this.filteredCompanies = this.companies.filter(c =>
      this.toStr(c.companyName).toLowerCase().includes(q) ||
      this.toStr(c.companyId).toLowerCase().includes(q)
    );
    this.resetCompaniesPagination();
  }

  isExpanded(companyId: any): boolean {
    return this.expandedCompanyIds.has(Number(companyId));
  }

  toggleExpand(company: any): void {
    const id = Number(company.companyId);
    if (this.isExpanded(id)) {
      this.expandedCompanyIds.delete(id);
      return;
    }
    this.expandedCompanyIds.add(id);
    if (!this.employeesByCompany.has(id)) this.loadEmployeesForCompany(id);
  }

  // ── Employee sub-table filter / pagination helpers ─────────────

  filterEmployee(companyId: number, query: string): void {
    this.empFilterMap[companyId] = query;
    this.empCurrentPageMap[companyId] = 1;
  }

  setEmpPage(companyId: number, page: number): void {
    this.empCurrentPageMap[companyId] = page;
  }

  setEmpPageSize(companyId: number, size: number): void {
    this.empPageSizeMap[companyId] = size;
    this.empCurrentPageMap[companyId] = 1;
  }

  getFilteredEmployees(companyId: number): EmployeeWalletSummary[] {
    const all = this.employeesByCompany.get(companyId) ?? [];
    const q = (this.empFilterMap[companyId] ?? '').toLowerCase().trim();
    if (!q) return all;
    return all.filter(e =>
      e.employeeId.toLowerCase().includes(q) ||
      e.employeeName.toLowerCase().includes(q)
    );
  }

  getPagedEmployees(companyId: number): EmployeeWalletSummary[] {
    const filtered = this.getFilteredEmployees(companyId);
    const pageSize = this.empPageSizeMap[companyId] ?? 5;
    const page = this.empCurrentPageMap[companyId] ?? 1;
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }

  getEmpTotalPages(companyId: number): number {
    const total = this.getFilteredEmployees(companyId).length;
    const pageSize = this.empPageSizeMap[companyId] ?? 5;
    return Math.max(1, Math.ceil(total / pageSize));
  }

  getEmpPageNumbers(companyId: number): number[] {
    const totalPages = this.getEmpTotalPages(companyId);
    const current = this.empCurrentPageMap[companyId] ?? 1;
    const maxVisible = 5;
    let start = Math.max(1, current - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);
    const pages: number[] = [];
    for (let p = start; p <= end; p++) pages.push(p);
    return pages;
  }

  getEmpCurrentPage(companyId: number): number {
    return this.empCurrentPageMap[companyId] ?? 1;
  }

  getEmpPageSize(companyId: number): number {
    return this.empPageSizeMap[companyId] ?? 5;
  }

  getEmpFilteredCount(companyId: number): number {
    return this.getFilteredEmployees(companyId).length;
  }

  getEmpShowFrom(companyId: number): number {
    const filtered = this.getFilteredEmployees(companyId);
    if (filtered.length === 0) return 0;
    const page = this.getEmpCurrentPage(companyId);
    const pageSize = this.getEmpPageSize(companyId);
    return (page - 1) * pageSize + 1;
  }

  getEmpShowTo(companyId: number): number {
    const filtered = this.getFilteredEmployees(companyId);
    if (filtered.length === 0) return 0;
    const page = this.getEmpCurrentPage(companyId);
    const pageSize = this.getEmpPageSize(companyId);
    return Math.min(page * pageSize, filtered.length);
  }

  private loadEmployeesForCompany(companyId: number): void {
    this.isEmployeesLoadingMap[companyId] = true;
    this.employeesErrorMap[companyId] = '';

    this.dataSubscriber$.add(
      this.statisticsService.getCompanyEmployeeWalletWithdrawalTotals(companyId, this.companyStartDate, this.companyEndDate).subscribe({
        next: (response: any) => {
          if (response?.success) {
            this.employeesByCompany.set(companyId, (response.data ?? []) as any[]);
          } else {
            this.employeesByCompany.set(companyId, []);
            this.employeesErrorMap[companyId] = response?.message || 'Could not load employee summaries for this company.';
          }
          this.isEmployeesLoadingMap[companyId] = false;
        },
        error: (err) => {
          this.employeesByCompany.set(companyId, []);
          this.employeesErrorMap[companyId] = this.getUserFriendlyError(err, 'Could not load employee summaries for this company.');
          this.isEmployeesLoadingMap[companyId] = false;
        }
      })
    );
  }

  private updateCompaniesPage(): void {
    if (this.companiesPage < 1) this.companiesPage = 1;
    const start = (this.companiesPage - 1) * this.companiesPageSize;
    this.pagedCompanies = this.filteredCompanies.slice(start, start + this.companiesPageSize);
  }
  private resetCompaniesPagination(): void {
    this.companiesPage = 1;
    this.updateCompaniesPage();
  }
  onCompaniesPageChange(paginatedData: any[]): void {
    this.pagedCompanies = paginatedData;
  }

  // ───────────────────────────────────────────────────────────────
  // Employee withdrawals (offcanvas)
  // ───────────────────────────────────────────────────────────────
  openEmployee(company: any, employee: any): void {
    this.selectedCompany = company;
    this.selectedEmployee = employee;
    this.withdrawalSearchText = '';
    this.filteredWithdrawals = [];
    this.resetWithdrawalsPagination();
    this.fetchEmployeeWithdrawals();
  }

  fetchEmployeeWithdrawals(): void {
    if (!this.selectedCompany || !this.selectedEmployee) return;
    const companyId = Number(this.selectedCompany.companyId);
    const employeeId = String(this.selectedEmployee.employeeId);
    this.isWithdrawalsLoading = true;
    this.withdrawalsError = '';

    this.dataSubscriber$.add(
      this.statisticsService.getEmployeeWalletWithdrawals(companyId, employeeId, this.companyStartDate, this.companyEndDate).subscribe({
        next: (response: any) => {
          if (response?.success) {
            const rows = (response.data ?? []) as any[];
            // newest first by date if present
            this.withdrawals = rows.sort((a, b) => {
              const da = new Date(a.transactionCreatedDate || a.transactionDate || a.date || a.createdDate).getTime();
              const db = new Date(b.transactionCreatedDate || b.transactionDate || b.date || b.createdDate).getTime();
              return db - da;
            });
            this.performWithdrawalSearch();
          } else {
            this.withdrawalsError = response?.message || 'No withdrawals found for the selected period.';
            this.withdrawals = [];
            this.performWithdrawalSearch();
          }
          this.isWithdrawalsLoading = false;
        },
        error: (err) => {
          this.withdrawalsError = this.getUserFriendlyError(err, 'Could not load withdrawals.');
          this.isWithdrawalsLoading = false;
        }
      })
    );
  }

  performWithdrawalSearch(): void {
    const searchText = (this.withdrawalSearchText || '').trim().toLowerCase();
    if (!searchText) {
      this.filteredWithdrawals = [...this.withdrawals];
      this.resetWithdrawalsPagination();
      return;
    }
    this.filteredWithdrawals = this.withdrawals.filter(withdrawal =>
      Object.values(withdrawal).some(val =>
        String(val).toLowerCase().includes(searchText)
      )
    );
    this.resetWithdrawalsPagination();
  }

  private updateWithdrawalsPage(): void {
    if (this.withdrawalsPage < 1) this.withdrawalsPage = 1;
    const start = (this.withdrawalsPage - 1) * this.withdrawalsPageSize;
    this.pagedWithdrawals = this.filteredWithdrawals.slice(start, start + this.withdrawalsPageSize);
  }
  private resetWithdrawalsPagination(): void {
    this.withdrawalsPage = 1;
    this.updateWithdrawalsPage();
  }
  onWithdrawalPageChange(paginatedData: any[]): void {
    this.pagedWithdrawals = paginatedData;
  }

  // ───────────────────────────────────────────────────────────────
  // Reports tab
  // ───────────────────────────────────────────────────────────────
  reportsCompanies: any[] = [];
  selectedReportCompany: any = null;
  reportStartDate: string = '';
  reportEndDate: string = '';
  isReportsCompaniesLoading = false;
  reportsCompaniesError = '';
  isDownloading: Record<ReportDownloadType, boolean> = {
    walletSummary: false,
    walletIn: false,
    walletOut: false
  };
  reportDownloadErrors: Record<ReportDownloadType, string> = {
    walletSummary: '',
    walletIn: '',
    walletOut: ''
  };

  get maxDate(): string {
    const today = new Date();
    return today.toISOString().split('T')[0];
  }

  // Date validation helper
  get minEndDate(): string {
    if (!this.reportStartDate) {
      return '';
    }

    const nextDay = new Date(this.reportStartDate);
    nextDay.setDate(nextDay.getDate() + 1);

    return nextDay.toISOString().split('T')[0];
  }


  setActiveTab(tab: 'walletStatus' | 'reports'): void {
    if (this.activeTab === tab) {
      return;
    }
    this.activeTab = tab;

    if (tab === 'reports') {
      this.fetchReportsCompanies();
    }
  }

  fetchReportsCompanies(): void {
    if (this.reportsCompanies.length > 0) {
      return; // Already loaded
    }
    this.isReportsCompaniesLoading = true;
    this.reportsCompaniesError = '';

    this.dataSubscriber$.add(
      this.companyService.getExistingCompanies().subscribe({
        next: (response: any) => {
          if (response?.success) {
            this.reportsCompanies = (response.data ?? []) as any[];
          } else {
            this.reportsCompaniesError = response?.message || 'Could not load companies for report download.';
            this.reportsCompanies = [];
          }
          this.isReportsCompaniesLoading = false;
        },
        error: (err) => {
          this.reportsCompaniesError = this.getUserFriendlyError(err, 'Could not load companies for report download.');
          this.reportsCompanies = [];
          this.isReportsCompaniesLoading = false;
        }
      })
    );
  }

  canExportReport(): boolean {
    // return !!(
    //   this.selectedReportCompany &&
    //   this.reportStartDate &&
    //   this.reportEndDate &&
    //   new Date(this.reportStartDate) <= new Date(this.reportEndDate)
    // );
    return !!(
      this.selectedReportCompany &&
      this.reportStartDate &&
      this.reportEndDate &&
      new Date(this.reportStartDate) <= new Date(this.reportEndDate)
    );
  }

  hasInvalidDateRange(): boolean {
    if (!this.reportStartDate || !this.reportEndDate) {
      return false;
    }
    const startDate = new Date(this.reportStartDate);
    const endDate = new Date(this.reportEndDate);

    // Invalid if end date is before OR equal to start date
    return endDate <= startDate;
  }

  onStartDateChange(): void {
    if (this.reportStartDate) {
      // If end date exists and is before or equal to new start date, clear it and show warning
      if (this.reportEndDate) {
        const startDate = new Date(this.reportStartDate);
        const endDate = new Date(this.reportEndDate);

        if (endDate <= startDate) {
          this.toaster.warning('End date must be after start date. End date has been cleared.', 'Warning');
          this.reportEndDate = '';
        }
      }
    }
  }

  onEndDateChange(): void {
    // Check if end date is selected without start date
    if (!this.reportStartDate && this.reportEndDate) {
      this.toaster.warning('Please select start date first.', 'Warning');
      this.reportEndDate = '';
      return;
    }

    // If end date is before or equal to start date, clear it and show warning
    if (this.reportEndDate && this.reportStartDate) {
      const startDate = new Date(this.reportStartDate);
      const endDate = new Date(this.reportEndDate);

      if (endDate <= startDate) {
        this.toaster.warning('End date must be after start date.', 'Warning');
        this.reportEndDate = '';
      }
    }
  }

  resetReportFilters(): void {
    this.selectedReportCompany = null;
    this.reportStartDate = '';
    this.reportEndDate = '';
    this.reportCompanySearchTerm.set('');
    this.reportCompanyDropdownOpen = false;
  }

  // downloadReport(type: ReportDownloadType): void {
  //   if (!this.canExportReport()) {
  //     return;
  //   }

  //   const company = this.selectedReportCompany;
  //   if (!company) {
  //     return;
  //   }

  //   const companyId = company.companyId;
  //   const companyName = company.companyName ?? '';
  //   const startDate = this.reportStartDate;
  //   const endDate = this.reportEndDate;

  //   this.reportDownloadErrors[type] = '';
  //   this.isDownloading[type] = true;

  //   let request$: Observable<HttpResponse<Blob>> | null = null;
  //   switch (type) {
  //     case 'walletSummary':
  //       request$ = this.statisticsService.downloadWalletSummaryReport(companyId, companyName, startDate, endDate);
  //       break;
  //     case 'walletIn':
  //       request$ = this.statisticsService.downloadWageWithdrawalHistoryReport(companyId, companyName, startDate, endDate);
  //       break;
  //     case 'walletOut':
  //       request$ = this.statisticsService.downloadWalletWithdrawalTransactionsReport(companyId, companyName, startDate, endDate);
  //       break;
  //     default:
  //       request$ = null;
  //   }

  //   if (!request$) {
  //     this.isDownloading[type] = false;
  //     return;
  //   }

  //   request$
  //     .pipe(finalize(() => {
  //       this.isDownloading[type] = false;
  //     }))
  //     .subscribe({
  //       next: (response: HttpResponse<Blob>) => {
  //         const blob = response.body;
  //         if (!blob || blob.size === 0) {
  //           this.reportDownloadErrors[type] = 'Received empty file from the server.';
  //           return;
  //         }

  //         const contentType = (response.headers?.get('content-type') || '').toLowerCase();
  //         if (contentType.includes('application/json') || contentType.includes('text/plain')) {
  //           blob.text().then(txt => {
  //             const parsedMessage = this.parseServerErrorText(txt) || 'Failed to generate the report.';
  //             this.reportDownloadErrors[type] = `${this.reportTypeLabels[type]}: ${parsedMessage}`;
  //           }).catch(() => {
  //             this.reportDownloadErrors[type] = `${this.reportTypeLabels[type]}: Failed to generate the report.`;
  //           });
  //           return;
  //         }

  //         const filename = this.extractFileName(response) || this.buildFallbackFileName(type, companyName, startDate, endDate);
  //         this.downloadBlob(blob, filename);
  //       },
  //       error: (err) => {
  //         const errorBlob = err?.error;
  //         if (errorBlob instanceof Blob) {
  //           errorBlob.text().then((txt: string) => {
  //             const serverMsg = this.parseServerErrorText(txt);
  //             const message = serverMsg || this.getUserFriendlyError(err, 'Failed to download report.');
  //             this.reportDownloadErrors[type] = `${this.reportTypeLabels[type]}: ${message}`;
  //           }).catch(() => {
  //             const message = this.getUserFriendlyError(err, 'Failed to download report.');
  //             this.reportDownloadErrors[type] = `${this.reportTypeLabels[type]}: ${message}`;
  //           });
  //         } else {
  //           const message = this.getUserFriendlyError(err, 'Failed to download report.');
  //           this.reportDownloadErrors[type] = `${this.reportTypeLabels[type]}: ${message}`;
  //         }
  //       }
  //     });
  // }

  downloadReport(type: ReportDownloadType): void {
    if (!this.canExportReport()) return;

    const company = this.selectedReportCompany;
    if (!company) return;

    const isAllCompanies = company.companyId === ALL_COMPANIES_SENTINEL.companyId;
    const startDate = this.reportStartDate;
    const endDate = this.reportEndDate;

    this.reportDownloadErrors[type] = '';
    this.isDownloading[type] = true;

    let request$: Observable<HttpResponse<Blob>> | null = null;

    if (isAllCompanies) {
      // ── All-companies multi-sheet download ──────────────────────────────────
      switch (type) {
        case 'walletSummary':
          request$ = this.statisticsService.downloadAllCompaniesWalletSummaryReport(startDate, endDate);
          break;
        case 'walletIn':
          request$ = this.statisticsService.downloadAllCompaniesWageWithdrawalHistoryReport(startDate, endDate);
          break;
        case 'walletOut':
          request$ = this.statisticsService.downloadAllCompaniesWalletWithdrawalTransactionsReport(startDate, endDate);
          break;
        default:
          request$ = null;
      }
    } else {
      // ── Single-company download (existing logic) ────────────────────────────
      const companyId = company.companyId;
      const companyName = company.companyName ?? '';

      switch (type) {
        case 'walletSummary':
          request$ = this.statisticsService.downloadWalletSummaryReport(companyId, companyName, startDate, endDate);
          break;
        case 'walletIn':
          request$ = this.statisticsService.downloadWageWithdrawalHistoryReport(companyId, companyName, startDate, endDate);
          break;
        case 'walletOut':
          request$ = this.statisticsService.downloadWalletWithdrawalTransactionsReport(companyId, companyName, startDate, endDate);
          break;
        default:
          request$ = null;
      }
    }

    if (!request$) {
      this.isDownloading[type] = false;
      return;
    }

    request$
      .pipe(finalize(() => { this.isDownloading[type] = false; }))
      .subscribe({
        next: (response: HttpResponse<Blob>) => {
          const blob = response.body;
          if (!blob || blob.size === 0) {
            this.reportDownloadErrors[type] = 'Received empty file from the server.';
            return;
          }

          const contentType = (response.headers?.get('content-type') || '').toLowerCase();
          if (contentType.includes('application/json') || contentType.includes('text/plain')) {
            blob.text().then(txt => {
              const parsedMessage = this.parseServerErrorText(txt) || 'Failed to generate the report.';
              this.reportDownloadErrors[type] = `${this.reportTypeLabels[type]}: ${parsedMessage}`;
            }).catch(() => {
              this.reportDownloadErrors[type] = `${this.reportTypeLabels[type]}: Failed to generate the report.`;
            });
            return;
          }

          // Build fallback filename using "All_Companies" when applicable.
          const displayName = isAllCompanies ? 'All_Companies' : (company.companyName ?? '');
          const filename =
            this.extractFileName(response) ||
            this.buildFallbackFileName(type, displayName, startDate, endDate);

          this.downloadBlob(blob, filename);
        },
        error: (err) => {
          const errorBlob = err?.error;
          if (errorBlob instanceof Blob) {
            errorBlob.text().then((txt: string) => {
              const serverMsg = this.parseServerErrorText(txt);
              const message = serverMsg || this.getUserFriendlyError(err, 'Failed to download report.');
              this.reportDownloadErrors[type] = `${this.reportTypeLabels[type]}: ${message}`;
            }).catch(() => {
              const message = this.getUserFriendlyError(err, 'Failed to download report.');
              this.reportDownloadErrors[type] = `${this.reportTypeLabels[type]}: ${message}`;
            });
          } else {
            const message = this.getUserFriendlyError(err, 'Failed to download report.');
            this.reportDownloadErrors[type] = `${this.reportTypeLabels[type]}: ${message}`;
          }
        }
      });
  }

  // ───────────────────────────────────────────────────────────────
  // Helpers
  // ───────────────────────────────────────────────────────────────
  private toStr(v: any): string {
    return v === null || v === undefined ? '' : String(v);
  }

  formatDateTime(iso?: string | null): string {
    if (!iso) return '';
    return iso.split('T')[0];
  }


  private extractFileName(response: HttpResponse<Blob>): string | null {
    const disposition = response.headers?.get('content-disposition') || response.headers?.get('Content-Disposition');
    if (!disposition) {
      return null;
    }
    const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(disposition);
    if (!match) {
      return null;
    }
    const encoded = match[1];
    const plain = match[2];
    try {
      return encoded ? decodeURIComponent(encoded) : plain;
    } catch (error) {
      console.warn('Failed to decode filename from Content-Disposition header.', error);
      return plain || null;
    }
  }

  // private buildFallbackFileName(type: ReportDownloadType, companyName: string, startDate: string, endDate: string): string {
  //   const sanitizedCompany = this.sanitizeForFilename(companyName || 'company');
  //   const label = this.sanitizeForFilename(this.reportTypeLabels[type]);
  //   return `${label}_${sanitizedCompany}_${startDate}_to_${endDate}.xlsx`;
  // }
  private buildFallbackFileName(type: ReportDownloadType, displayName: string, startDate: string, endDate: string): string {
    const sanitized = this.sanitizeForFilename(displayName || 'company');
    const label = this.sanitizeForFilename(this.reportTypeLabels[type]);
    return `${label}_${sanitized}_${startDate}_to_${endDate}.xlsx`;
  }

  private downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || 'report.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  private sanitizeForFilename(name: string): string {
    return (name || '').replace(/[\\/:*?"<>|]/g, '_').replace(/\s+/g, '_');
  }

  private getUserFriendlyError(err: any, fallback: string): string {
    const status = err?.status;
    const serverMessage = err?.error?.message || err?.message || '';

    if (status === 0) {
      return 'Network error. Please check your internet connection and try again.';
    }
    if (status === 400 || status === 422) {
      return serverMessage || 'Invalid request. Please adjust your inputs and try again.';
    }
    if (status === 401 || status === 403) {
      return 'You are not authorized to perform this action. Please sign in again or contact an administrator.';
    }
    if (status === 404) {
      return serverMessage || 'No data found for the selected filters.';
    }
    if (status >= 500) {
      return 'Server error. Please try again later or contact support if the issue persists.';
    }
    return serverMessage || fallback;
  }

  private parseServerErrorText(txt: string): string {
    if (!txt) return '';
    try {
      const obj = JSON.parse(txt);
      if (obj) {
        if (typeof obj.message === 'string' && obj.message) return obj.message;
        if (typeof obj.error === 'string' && obj.error) return obj.error;
        if (obj.error && typeof obj.error.message === 'string' && obj.error.message) return obj.error.message;
        if (typeof obj.title === 'string' && obj.title) return obj.title;
        const compact = JSON.stringify(obj);
        return compact.length > 300 ? compact.substring(0, 300) + '…' : compact;
      }
    } catch {
      // not JSON
    }
    const trimmed = txt.trim();
    return trimmed.length > 300 ? trimmed.substring(0, 300) + '…' : trimmed;
  }

  displayWalletUpdateDate(dateInput: string | Date | null | undefined): string {
    if (!dateInput) {
      return 'N/A';
    }

    let date: Date;
    if (typeof dateInput === 'string') {
      // Handle ISO string from backend (e.g., "2026-01-01T00:00:00" or "0001-01-01T00:00:00")
      date = new Date(dateInput);
    } else if (dateInput instanceof Date) {
      date = dateInput;
    } else {
      return 'N/A';
    }

    // Check if the date is invalid or is the .NET MinValue (year 1)
    if (isNaN(date.getTime()) || date.getFullYear() <= 1) {
      return 'N/A';
    }

    // Format as yyyy-MM-dd
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  // Helper method to get transaction details display
  getTransactionDetails(transaction: any): string {
    switch (transaction.transactionType) {
      case 'PAYANYONE_TRANSFER':
      case 'BANK_TRANSFER':
        return `${transaction.bankName || '-'} (${transaction.bankAccountNumber || '-'})`;

      case 'CTCQR_TRANSFER_IN':
        return `To: ${transaction.receiverUserName || '-'}`;

      case 'CTCQR_TRANSFER_OUT':
        return `From: ${transaction.receiverUserName || '-'}`;

      case 'BILL_PAYMENT':
        return `Bill Payment (${transaction.bankAccountNumber || '-'})`;

      case 'EWW':
        return `Wallet Top Up (Cycle: ${transaction.salaryCycleId || '-'})`;

      default:
        return '-';
    }
  }

  // Helper method to check if EWW has additional info
  hasEWWDetails(transaction: any): boolean {
    return transaction.transactionType === 'EWW' &&
      (transaction.salaryCycleId || transaction.finalBalance);
  }

  getTransactionTypeLabel(transactionType: string): string {
    const labels: { [key: string]: string } = {
      'PAYANYONE_TRANSFER': 'Bank Transfer',
      'BANK_TRANSFER': 'Bank Transfer',
      'CTCQR_TRANSFER_IN': 'CTCQR In',
      'CTCQR_TRANSFER_OUT': 'CTCQR Out',
      'CTCQR': 'CTCQR',
      'BILL_PAYMENT': 'Bill Payment',
      'EWW': 'EWW'
    };

    return labels[transactionType] || transactionType;
  }

  getTransactionTypeBadgeClass(transactionType: string): string {
    const badgeClasses: { [key: string]: string } = {
      'PAYANYONE_TRANSFER': 'bg-brown',
      'BANK_TRANSFER': 'bg-brown',
      'CTCQR_TRANSFER_IN': 'bg-success',
      'CTCQR_TRANSFER_OUT': 'bg-warning text-dark',
      'CTCQR': 'bg-secondary',
      'BILL_PAYMENT': 'bg-info',
      'EWW': 'bg-primary',
      'REVERSE': 'bg-danger'
    };

    return badgeClasses[transactionType] || 'bg-secondary';
  }

  isCTCQRTransaction(transactionType: string): boolean {
    return ['CTCQR_TRANSFER_IN', 'CTCQR_TRANSFER_OUT', 'CTCQR_TRANSFER', 'CTCQR'].includes(transactionType);
  }

  // Alternative: Get direction label
  getCTCQRDirection(transactionType: string): string {
    const directions: { [key: string]: string } = {
      'CTCQR_TRANSFER_IN': 'Sent To',
      'CTCQR_TRANSFER_OUT': 'Received From',
      'CTCQR_TRANSFER': 'Transfer',
      'CTCQR': 'Transfer'
    };

    return directions[transactionType] || '';
  }

  getSelectedReportCompanyName(): string {
    if (!this.selectedReportCompany) return '';
    if (this.selectedReportCompany.companyId === ALL_COMPANIES_SENTINEL.companyId) {
      return 'All Companies';
    }
    return `${this.selectedReportCompany.companyName} (${this.selectedReportCompany.companyId})`;
  }

  openReportCompanyDropdown(): void {
    this.reportCompanyDropdownOpen = true;
    this.reportCompanySearchTerm.set('');
  }

  onReportCompanySearchChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.reportCompanySearchTerm.set(input.value);
    this.reportCompanyDropdownOpen = true;
  }

  selectReportCompany(company: any): void {
    this.selectedReportCompany = company ?? null;
    this.reportCompanySearchTerm.set('');
    this.reportCompanyDropdownOpen = false;
  }

  onReportCompanyBlur(): void {
    setTimeout(() => {
      this.reportCompanyDropdownOpen = false;
      this.reportCompanySearchTerm.set('');
    }, 200);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.searchable-dropdown')) {
      this.reportCompanyDropdownOpen = false;
      this.reportCompanySearchTerm.set('');
    }
  }

  ngOnDestroy(): void {
    this.dataSubscriber$.unsubscribe();
  }
}
